/**
 * Package API functions.
 */

import apiClient from './client';
import type {
  BSPPackage,
  BSPPackageDetail,
  PackageCreate,
  PackageUpdate,
  PackageFileCreate,
  PackageFile,
  ProjectFile,
  PreviousGitSource,
  PaginatedResponse,
  PackageQueryParams,
  OperationLog,
  PackageFileTypes,
} from '@/types';

/**
 * List packages with optional filters.
 */
export const listPackages = async (
  params?: PackageQueryParams
): Promise<PaginatedResponse<BSPPackage>> => {
  const response = await apiClient.get<PaginatedResponse<BSPPackage>>('/packages/', { params });
  return response.data;
};

/**
 * Get package details.
 */
export const getPackage = async (id: number): Promise<BSPPackageDetail> => {
  const response = await apiClient.get<BSPPackageDetail>(`/packages/${id}/`);
  return response.data;
};

/**
 * Create a new package.
 */
export const createPackage = async (data: PackageCreate | Record<string, unknown>): Promise<BSPPackageDetail> => {
  const response = await apiClient.post<BSPPackageDetail>('/packages/', data);
  return response.data;
};

/**
 * Update a package.
 */
export const updatePackage = async (
  id: number,
  data: PackageUpdate | Record<string, unknown>
): Promise<BSPPackageDetail> => {
  const response = await apiClient.put<BSPPackageDetail>(`/packages/${id}/`, data);
  return response.data;
};

/**
 * Partially update a package (PATCH).
 */
export const patchPackage = async (
  id: number,
  data: Record<string, unknown>
): Promise<BSPPackageDetail> => {
  const response = await apiClient.patch<BSPPackageDetail>(`/packages/${id}/`, data);
  return response.data;
};

/**
 * Delete a package (soft delete).
 */
export const deletePackage = async (id: number): Promise<void> => {
  await apiClient.delete(`/packages/${id}/`);
};

/**
 * List files for a package.
 */
export const listPackageFiles = async (packageId: number): Promise<PackageFile[]> => {
  const response = await apiClient.get<PackageFile[]>(`/packages/${packageId}/files/`);
  return response.data;
};

/**
 * Get available file types for a package (for BCR comparison workflow).
 */
export const getPackageFileTypes = async (packageId: number): Promise<PackageFileTypes> => {
  const response = await apiClient.get<PackageFileTypes>(`/packages/${packageId}/file-types/`);
  return response.data;
};

/**
 * Add a file to a package.
 */
export const addPackageFile = async (
  packageId: number,
  data: PackageFileCreate
): Promise<PackageFile> => {
  const response = await apiClient.post<PackageFile>(`/packages/${packageId}/files/`, data);
  return response.data;
};

/**
 * Delete a file from a package.
 * Requires fileSource to confirm the file's origin and prevent accidental cross-source deletion.
 */
export const deletePackageFile = async (
  packageId: number,
  fileId: number,
  fileSource: 'supplier' | 'momenta'
): Promise<void> => {
  await apiClient.delete(`/packages/${packageId}/files/${fileId}/`, {
    params: { file_source: fileSource },
  });
};

/**
 * Update file type of a package file.
 * Requires fileSource to confirm the file's origin and prevent accidental cross-source edits.
 */
export const updatePackageFile = async (
  packageId: number,
  fileId: number,
  fileTypeId: number,
  fileSource: 'supplier' | 'momenta'
): Promise<PackageFile> => {
  const response = await apiClient.patch<PackageFile>(
    `/packages/${packageId}/files/${fileId}/`,
    { file_type_id: fileTypeId, file_source: fileSource }
  );
  return response.data;
};

/**
 * Delete release note document from a package.
 */
export const deleteReleaseNote = async (packageId: number): Promise<void> => {
  await apiClient.delete(`/packages/${packageId}/release-note/`);
};

/**
 * Delete test report document from a package.
 */
export const deleteTestReport = async (packageId: number): Promise<void> => {
  await apiClient.delete(`/packages/${packageId}/test-report/`);
};

export const failReleaseNoteUpload = async (packageId: number): Promise<void> => {
  await apiClient.patch(`/packages/${packageId}/release-note/fail/`);
};

export const failTestReportUpload = async (packageId: number): Promise<void> => {
  await apiClient.patch(`/packages/${packageId}/test-report/fail/`);
};

/**
 * List existing files across packages in a project, filtered by file type.
 */
export const getProjectFiles = async (
  projectId: number,
  fileTypeId: number,
  excludePackageId?: number
): Promise<ProjectFile[]> => {
  const params: Record<string, number> = { project_id: projectId, file_type_id: fileTypeId };
  if (excludePackageId) params.exclude_package_id = excludePackageId;
  const response = await apiClient.get<ProjectFile[]>('/packages/project-files/', { params });
  return response.data;
};

/**
 * Get git source info from previous packages in the same project.
 */
export const getPreviousGitSources = async (
  projectId: number,
  excludePackageId?: number
): Promise<PreviousGitSource[]> => {
  const params: Record<string, number> = { project_id: projectId };
  if (excludePackageId) params.exclude_package_id = excludePackageId;
  const response = await apiClient.get<PreviousGitSource[]>('/packages/previous-git-sources/', { params });
  return response.data;
};

/**
 * Get operation logs for a package.
 */
export const getPackageLogs = async (packageId: number): Promise<OperationLog[]> => {
  const response = await apiClient.get<OperationLog[]>(`/packages/${packageId}/logs/`);
  return response.data;
};

/**
 * Update package upload status.
 */
export const updatePackageUploadStatus = async (
  packageId: number,
  uploadStatus: 'uploading' | 'done' | 'failed',
  reason?: string
): Promise<{ upload_status: string }> => {
  const response = await apiClient.patch<{ upload_status: string }>(
    `/packages/${packageId}/upload-status/`,
    { upload_status: uploadStatus, ...(reason ? { reason } : {}) }
  );
  return response.data;
};

/**
 * Update a package file after deferred upload completes.
 */
export const completePackageFileUpload = async (
  packageId: number,
  fileId: number,
  data: { file_url: string; object_key: string; file_size: number; checksum: string; file_name?: string; upload_status?: string; upload_started_at?: string | null; upload_finished_at?: string | null; avg_speed_mbps?: number | null }
): Promise<PackageFile> => {
  const response = await apiClient.patch<PackageFile>(
    `/packages/${packageId}/files/${fileId}/upload-complete/`,
    data
  );
  return response.data;
};

/**
 * Mark a package file upload as failed.
 */
export const failPackageFileUpload = async (
  packageId: number,
  fileId: number
): Promise<PackageFile> => {
  const response = await apiClient.patch<PackageFile>(
    `/packages/${packageId}/files/${fileId}/upload-complete/`,
    { upload_status: 'failed' }
  );
  return response.data;
};

/**
 * Set intermediate upload status (queued or uploading) for a package file.
 */
export const startPackageFileUpload = async (
  packageId: number,
  fileId: number,
  status: 'queued' | 'uploading'
): Promise<void> => {
  await apiClient.patch(
    `/packages/${packageId}/files/${fileId}/upload-complete/`,
    { upload_status: status }
  );
};

/**
 * Send a heartbeat to keep the file's updated_at fresh, preventing it from being
 * mistakenly marked as a stuck/abandoned upload by other browser sessions.
 */
export const heartbeatPackageFile = async (
  packageId: number,
  fileId: number,
  percent?: number,
  pendingObjectKey?: string,
): Promise<void> => {
  const body: Record<string, unknown> = {};
  if (percent !== undefined) body.percent = percent;
  if (pendingObjectKey) body.pending_object_key = pendingObjectKey;
  await apiClient.patch(`/packages/${packageId}/files/${fileId}/heartbeat/`, body);
};
